import type { Utilisateur } from '@/features/auth';
import { useAuthentificationStore } from '@/features/auth/authentification/stores/useAuthentificationStore';
import { SilentError } from '@/features/shared/types';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import type { App } from 'vue';
import type { Router } from 'vue-router'

const appInsights = new ApplicationInsights({
  config: {
    connectionString: import.meta.env.VITE_INSIGHT_CONNEXION,
    // Attention, il peut y avoir des erreur cors avec des bloquer de pub,
    // comme uBlock origin, ...
  },
});

appInsights.loadAppInsights();
appInsights.trackPageView();

// Intercepte les logs de console
(()=> {
  const originalError = console.error.bind(console)
  const originalWarn = console.warn.bind(console)

  console.error = (...args: unknown[]) => {
    const [first, ...rest] = args

    if (first instanceof SilentError) return

    // Appelle le console log originale
    originalError(...args)

    // Reconstitue une vraie Error si ce n'en est pas une
    const error = first instanceof Error ? first : new Error(`${first}`)

    setUser()
    appInsights.trackException({
      exception: error,
      properties: {
        errorName: error.name,
        errorMessage: error.message,
        errorCause: error.cause,
        errorTrace: `${error.stack}`,
        errorRest: JSON.stringify(rest),
      },
    })
  }

  console.warn = (...args: unknown[]) => {
    originalWarn(...args)

    setUser()
    appInsights.trackTrace({
      message: args.map(String).join(' '),
      severityLevel: SeverityLevel.Warning,
      properties: {
        warningMessage: args[0]?.toString(),
        warningArgs: JSON.stringify(args),
      },
    })
  }
})()

function setupPageTracking(router: Router) {
  router.beforeEach((route, _from) => {
    setUser()
    const name = route.name?.toString()
    appInsights.context.telemetryTrace.traceID = appInsights.core.getTraceCtx()?.getTraceId()
    appInsights.context.telemetryTrace.name = name
    appInsights.startTrackPage(name)
    // next()
  })

  router.afterEach((route) => {
    const name = route.name?.toString()
    const url = location.protocol + '//' + location.host + route.fullPath
    appInsights.stopTrackPage(name, url)
    appInsights.flush()
  })
}

function setUser() {
  const auth = useAuthentificationStore()
  if (auth.utilisateur) {
    const authenticatedId = auth.utilisateur.userId
    const accountId = auth.utilisateur.estInterne
      ? (auth.utilisateur as Utilisateur).impresonnaliseEmail
      : auth.utilisateur.email

    if (
      appInsights.context.user.accountId === accountId &&
      appInsights.context.user.authenticatedId === authenticatedId
    )
      return
    appInsights.setAuthenticatedUserContext(authenticatedId, accountId)
  } else {
    if (!appInsights.context.user.authenticatedId) return
    appInsights.clearAuthenticatedUserContext()
  }
}

export default {
  install(app: App<Element>, options: { router: Router }) {
    // Configure les handler VueJS
    app.config.errorHandler =
      (err, _component, info) =>
        console.error(err, { info })
    app.config.warnHandler =
      (msg, _component, trace) =>
        console.warn(msg, { trace })

    // Configure la navigation
    const { router } = options
    if (router) {
      setupPageTracking(router)
    }
  },
}




